import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ZohoOAuthGrant from "./pages/ZohoOAuthGrant";
import { Toaster } from "react-hot-toast";
import logoWhite from "./images/logo-white.png";
import { auth, db } from "./firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  sendPasswordResetEmail,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  addDoc,
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import {
  Home,
  Topics,
  Lessons,
  Videos,
  TopicLessons,
  LessonOverview,
  LessonSteps,
  VideoOverview,
  Artifacts,
} from "./user";
import {
  Users,
  Organizations,
  OrganizationEntry,
  Lessons as AdminLessons,
  LessonEntry,
  Videos as AdminVideos,
  VideoEntry,
  Settings,
  Stats,
} from "./super_admin";
import { AdminDashboard } from "./admin";
import AuthenticatedLayout from "./components/AuthenticatedLayout";
import ProtectedAdminRoute from "./components/ProtectedAdminRoute";
import ProtectedSuperAdminRoute from "./components/ProtectedSuperAdminRoute";
import ScrollToTop from "./components/ScrollToTop";
import { US_STATES } from "./utils/states";
import { validateInvite, redeemInvite } from "./utils/invites";
import { pushLibraryAdminToZoho } from "./utils/zohoSync";

const TRIAL_ORG_ID = "qSbV9m9BKNmc7D1dAH2F";
const API_BASE =
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" && window.location.port === "3002"
    ? "http://localhost:5001"
    : "");

// Fire-and-forget; never blocks or throws
const notifyTrialSignup = (orgId, email) => {
  if (orgId !== TRIAL_ORG_ID) return;
  fetch(`${API_BASE}/api/notify-trial-signup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  }).catch(() => {});
};

function App() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [currentForm, setCurrentForm] = useState("login"); // "create" or "login"

  // Authentication state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState("user");

  // Organization selection state
  const [selectedState, setSelectedState] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [libraryCard, setLibraryCard] = useState("");

  // New state for targeted sign-up
  // eslint-disable-next-line no-unused-vars
  const [targetOrgId, setTargetOrgId] = useState("");
  const [targetOrg, setTargetOrg] = useState(null);
  const [isTargetedSignup, setIsTargetedSignup] = useState(false);
  const [isAdminSignup, setIsAdminSignup] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [adminSignupCode, setAdminSignupCode] = useState("");
  const [inviteCode, setInviteCode] = useState("");

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);

  // Helper function to convert Firebase errors to user-friendly messages
  const getFriendlyErrorMessage = (error) => {
    const errorCode = error.code;

    switch (errorCode) {
      case "auth/email-already-in-use":
        return "An account with this email already exists. You can reset your password if you've forgotten it.";
      case "auth/invalid-email":
        return "Please enter a valid email address.";
      case "auth/weak-password":
        return "Password should be at least 6 characters long.";
      case "auth/user-not-found":
        return "No account found with this email address.";
      case "auth/wrong-password":
        return "Incorrect password. Please try again.";
      case "auth/too-many-requests":
        return "Too many failed attempts. Please try again later.";
      case "auth/network-request-failed":
        return "Network error. Please check your connection and try again.";
      case "auth/popup-closed-by-user":
      case "auth/cancelled-popup-request":
        return "Sign-in was cancelled.";
      case "auth/account-exists-with-different-credential":
        return "An account already exists with this email. Please sign in with your password instead.";
      default:
        return "An error occurred. Please try again or contact support.";
    }
  };

  // Function to fetch organization details by ID
  const fetchOrganizationById = async (orgId) => {
    try {
      const orgDoc = await getDoc(doc(db, "organizations", orgId));
      if (orgDoc.exists()) {
        return { id: orgDoc.id, ...orgDoc.data() };
      }
      return null;
    } catch (error) {
      console.error("Error fetching organization:", error);
      return null;
    }
  };

  // Function to get URL parameters
  const getUrlParams = () => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      return {
        id: urlParams.get("id"),
        code: urlParams.get("code"),
        invite: urlParams.get("invite"),
      };
    }
    return { id: null, code: null, invite: null };
  };

  // Logout function
  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Track user login activity
  const trackUserLogin = async (userId) => {
    try {
      // Get user data to find organization_id
      const userDoc = await getDoc(doc(db, "users", userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();

        // Create login activity record
        const activityData = {
          created_date: new Date(),
          user_id: doc(db, "users", userId),
          organization_id: userData.organization_id,
          type: "user_login",
        };

        // Add to user_activity collection
        const activityRef = collection(db, "user_activity");
        await addDoc(activityRef, activityData);
      }
    } catch (error) {
      console.error("Error tracking login activity:", error);
      // Don't throw error - login tracking failure shouldn't prevent login
    }
  };

  // Fetch user type from Firestore
  const fetchUserType = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, "users", userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.user_type || "user";
      }
      return "user";
    } catch (error) {
      console.error("Error fetching user type:", error);
      return "user";
    }
  };

  // Monitor authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setUserType("user");
        return;
      }

      // Before allowing access, verify the user's organization is active
      try {
        const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
        if (!userDoc.exists()) {
          setUser(firebaseUser);
          const type = await fetchUserType(firebaseUser.uid);
          setUserType(type);
          return;
        }

        const userData = userDoc.data();
        let orgId =
          userData.organization_id?.id ||
          (userData.organization_id?.path
            ? userData.organization_id.path.split("/").pop()
            : null);
        if (typeof userData.organization_id === "string")
          orgId = userData.organization_id;

        if (!orgId) {
          // No org (e.g. super admin) - allow access
          setUser(firebaseUser);
          const type = await fetchUserType(firebaseUser.uid);
          setUserType(type);
          await trackUserLogin(firebaseUser.uid);
          return;
        }

        const orgDoc = await getDoc(doc(db, "organizations", orgId));
        if (!orgDoc.exists()) {
          setUser(firebaseUser);
          const type = await fetchUserType(firebaseUser.uid);
          setUserType(type);
          await trackUserLogin(firebaseUser.uid);
          return;
        }

        const orgData = orgDoc.data();
        if (orgData.active === false) {
          setError(
            "Your organization no longer has a license to Kuzko History",
          );
          await signOut(auth);
          return;
        }

        // Org is active - allow access
        setUser(firebaseUser);
        const type = await fetchUserType(firebaseUser.uid);
        setUserType(type);
        await trackUserLogin(firebaseUser.uid);
      } catch (err) {
        console.error("Error verifying organization:", err);
        setUser(firebaseUser);
        const type = await fetchUserType(firebaseUser.uid);
        setUserType(type);
        await trackUserLogin(firebaseUser.uid);
      }
    });

    return () => unsubscribe();
  }, []);

  // Handle targeted sign-up from URL parameter
  useEffect(() => {
    const handleTargetedSignup = async () => {
      const { id, code, invite } = getUrlParams();
      if (!user) {
        // Admin invite flow: ?invite=ABC123
        if (invite && invite.trim()) {
          try {
            const result = await validateInvite(invite.trim());
            if (result.valid && result.organization) {
              const org = result.organization;
              setTargetOrgId(org.id);
              setTargetOrg(org);
              setSelectedState(org.state || "");
              setSelectedOrgId(org.id);
              setOrganizations([org]);
              setIsTargetedSignup(true);
              setIsAdminSignup(true);
              setInviteCode(invite.trim().toUpperCase());
              setCurrentForm("create");
              setError("");
            } else {
              setError(
                "Invalid or expired invite. Please contact your administrator for a new invite.",
              );
              resetTargetedSignup();
            }
          } catch (err) {
            setError(
              err.message ||
                "Invalid or expired invite. Please contact your administrator.",
            );
            resetTargetedSignup();
          }
          return;
        }

        // User invite flow: ?id=orgId or ?id=orgId&code=admin_signup_code
        if (id) {
          setTargetOrgId(id);
          setIsTargetedSignup(true);

          const isAdminSignupFlag = !!code;
          setIsAdminSignup(isAdminSignupFlag);
          setAdminSignupCode(code || "");

          const org = await fetchOrganizationById(id);
          if (org && org.active === false) {
            setError(
              "Your organization no longer has a license to Kuzko History",
            );
            setIsTargetedSignup(false);
            setTargetOrgId("");
            setTargetOrg(null);
            setIsAdminSignup(false);
            setAdminSignupCode("");
            return;
          }
          if (org && org.state) {
            if (isAdminSignupFlag && org.admin_signup_code !== code) {
              setError(
                "Invalid admin signup code. Please contact your organization administrator for the correct code.",
              );
              setIsTargetedSignup(false);
              setTargetOrgId("");
              setTargetOrg(null);
              setIsAdminSignup(false);
              setAdminSignupCode("");
              return;
            }

            setTargetOrg(org);
            setSelectedState(org.state);
            setSelectedOrgId(org.id);
            setOrganizations([org]);
            setCurrentForm("create");
          } else {
            setError(
              "Invalid organization link or missing organization details. Please contact support.",
            );
            setIsTargetedSignup(false);
            setTargetOrgId("");
            setTargetOrg(null);
            setIsAdminSignup(false);
            setAdminSignupCode("");
          }
        }
      }
    };

    handleTargetedSignup();
  }, [user]);

  // Cleanup targeted sign-up state when user changes
  useEffect(() => {
    if (user) {
      resetTargetedSignup();
    }
  }, [user]);

  // Function to reset targeted sign-up state
  const resetTargetedSignup = () => {
    setTargetOrgId("");
    setTargetOrg(null);
    setIsTargetedSignup(false);
    setIsAdminSignup(false);
    setAdminSignupCode("");
    setInviteCode("");
  };

  // Authentication functions
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
      // Login successful - user will be redirected to home via useEffect

      clearFormData();
      resetTargetedSignup(); // Reset targeted sign-up state after successful login
    } catch (error) {
      setError(getFriendlyErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const firebaseUser = userCredential.user;

      const userDocRef = doc(db, "users", firebaseUser.uid);
      const existingDoc = await getDoc(userDocRef);

      if (!existingDoc.exists()) {
        // New user - sign them out and redirect to Create Account to collect org info
        await signOut(auth);
        setCurrentForm("create");
        setError(
          "No account found. Please create an account below and choose Sign in with Google.",
        );
      } else {
        clearFormData();
        resetTargetedSignup();
      }
    } catch (error) {
      setError(getFriendlyErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setError("");

    if (!isAdminSignup && !selectedState) {
      setError("Please select your state");
      return;
    }

    if (!selectedOrgId) {
      setError("Please select your location");
      return;
    }

    const selectedOrg = organizations.find((o) => o.id === selectedOrgId);

    if (selectedOrg && selectedOrg.active === false) {
      setError("Your organization no longer has a license to Kuzko History");
      return;
    }

    // Skip library card check for admin signups
    if (!isAdminSignup) {
      const authCode = selectedOrg?.authentication_code?.toString().trim();
      if (authCode) {
        if (!libraryCard.trim()) {
          const label = selectedOrg?.authentication_label || "Library Card";
          setError(`Please enter your ${label}`);
          return;
        }

        const prefixes = authCode
          .split(",")
          .map((prefix) => prefix.trim())
          .filter(Boolean);
        const cardNumber = libraryCard.trim();
        const isValid = prefixes.some((prefix) =>
          cardNumber.startsWith(prefix),
        );

        if (!isValid) {
          setError(
            "Your card number is not valid. Email hello@fierolearning.com for assistance.",
          );
          return;
        }
      }
    }

    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const firebaseUser = userCredential.user;

      const userDocRef = doc(db, "users", firebaseUser.uid);
      const existingDoc = await getDoc(userDocRef);

      if (!existingDoc.exists()) {
        const userData = {
          created_time: new Date().toISOString(),
          email: firebaseUser.email,
          first_name: firebaseUser.displayName?.split(" ")[0] || "",
          last_name:
            firebaseUser.displayName?.split(" ").slice(1).join(" ") || "",
          organization_id: doc(db, "organizations", selectedOrgId),
          organization_name: selectedOrg?.name || "",
          phone_number: "",
          uid: firebaseUser.uid,
          user_type: isAdminSignup ? "admin" : "user",
        };
        await setDoc(userDocRef, userData);

        if (isAdminSignup && inviteCode) {
          try {
            await redeemInvite(inviteCode, firebaseUser.uid);
          } catch (redeemErr) {
            console.error("Failed to redeem invite:", redeemErr);
          }
        }

        notifyTrialSignup(selectedOrgId, userData.email);
        if (isAdminSignup) {
          pushLibraryAdminToZoho(firebaseUser.uid).catch((err) => {
            console.error("Zoho CRM sync after Google admin signup failed:", err);
          });
        }
      }

      clearFormData();
      resetTargetedSignup();
    } catch (error) {
      setError(getFriendlyErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");

    if (!isAdminSignup && !selectedState) {
      setError("Please select your state");
      return;
    }

    if (!selectedOrgId) {
      setError("Please select your location");
      return;
    }

    const selectedOrg = organizations.find((o) => o.id === selectedOrgId);

    if (selectedOrg && selectedOrg.active === false) {
      setError("Your organization no longer has a license to Kuzko History");
      return;
    }

    // Skip library card check for admin signups
    if (!isAdminSignup) {
      const authCode = selectedOrg?.authentication_code?.toString().trim();
      if (authCode) {
        if (!libraryCard.trim()) {
          const label = selectedOrg?.authentication_label || "Library Card";
          setError(`Please enter your ${label}`);
          return;
        }

        const prefixes = authCode
          .split(",")
          .map((prefix) => prefix.trim())
          .filter(Boolean);
        const cardNumber = libraryCard.trim();
        const isValid = prefixes.some((prefix) =>
          cardNumber.startsWith(prefix),
        );

        if (!isValid) {
          setError(
            "Your card number is not valid. Email hello@fierolearning.com for assistance.",
          );
          return;
        }
      }
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const user = userCredential.user;

      // Create user document in users collection
      try {
        const userDocRef = doc(db, "users", user.uid);

        const userData = {
          created_time: new Date().toISOString(),
          email: user.email,
          first_name: "",
          last_name: "",
          organization_id: doc(db, "organizations", selectedOrgId),
          organization_name: selectedOrg?.name || "",
          phone_number: "",
          uid: user.uid,
          user_type: isAdminSignup ? "admin" : "user",
        };

        await setDoc(userDocRef, userData);

        // Redeem invite code if this was an admin invite signup
        if (isAdminSignup && inviteCode) {
          try {
            await redeemInvite(inviteCode, user.uid);
          } catch (redeemErr) {
            console.error("Failed to redeem invite:", redeemErr);
          }
        }

        notifyTrialSignup(selectedOrgId, userData.email);
        if (isAdminSignup) {
          pushLibraryAdminToZoho(user.uid).catch((err) => {
            console.error("Zoho CRM sync after admin signup failed:", err);
          });
        }
      } catch (firestoreError) {
        console.error("Firestore error:", firestoreError);
        console.error("Error code:", firestoreError.code);
        console.error("Error message:", firestoreError.message);
        setError(`Database error: ${firestoreError.message}`);
        return;
      }
      clearFormData();
      resetTargetedSignup(); // Reset targeted sign-up state after successful registration
    } catch (error) {
      console.error("Registration error:", error);
      setError(getFriendlyErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const clearFormData = () => {
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setLoginEmail("");
    setLoginPassword("");
    setError("");
    setLoading(false);

    // Only clear organization data if not a targeted sign-up
    if (!isTargetedSignup) {
      setSelectedState("");
      setOrganizations([]);
      setSelectedOrgId("");
      setLibraryCard("");
      setOrgsLoading(false);
    }

    setShowForgotPassword(false);
    setForgotPasswordEmail("");
    setForgotPasswordSuccess(false);
  };

  const handleFormSwitch = (formType) => {
    setCurrentForm(formType);
    // Only clear form data if not a targeted sign-up, or if switching away from create form
    if (!isTargetedSignup || formType !== "create") {
      clearFormData();
    } else {
      // For targeted sign-up, only clear the form fields but keep organization data
      setEmail("");
      setPassword("");
      setConfirmPassword("");
      setError("");
      setLoading(false);
      setShowForgotPassword(false);
      setForgotPasswordEmail("");
      setForgotPasswordSuccess(false);
      // Note: admin signup state is preserved for targeted signups
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setForgotPasswordLoading(true);

    try {
      await sendPasswordResetEmail(auth, forgotPasswordEmail);
      setForgotPasswordSuccess(true);
      setForgotPasswordEmail("");
    } catch (error) {
      setError(getFriendlyErrorMessage(error));
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setForgotPasswordSuccess(false);
    setForgotPasswordEmail("");
    setError("");
  };

  const renderCreateAccountForm = () => (
    <div className="text-left">
      <h3 className="text-xl sm:text-2xl font-bold text-primary-500 mb-2">
        {isAdminSignup ? "Create Admin Account" : "Create Account"}
      </h3>
      <p className="text-gray-600 mb-2 text-sm sm:text-base">
        <strong>{isTargetedSignup && targetOrg?.name}</strong>
      </p>
      <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
        {isAdminSignup
          ? "You're creating an admin account for this organization."
          : "Let's get started by filling out the form below."}
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
          {error}
        </div>
      )}

      <form className="space-y-3 sm:space-y-4" onSubmit={handleRegister}>
        {/* Email */}
        <div>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base touch-target"
            required
          />
        </div>

        {/* State - hidden for targeted sign-ups */}
        {!isTargetedSignup && (
          <div>
            <select
              className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white text-base touch-target"
              value={selectedState}
              onChange={async (e) => {
                const value = e.target.value;
                setSelectedState(value);
                // Reset downstream selections
                setSelectedOrgId("");
                setLibraryCard("");
                setOrganizations([]);
                if (!value) return;

                setOrgsLoading(true);
                try {
                  const orgsRef = collection(db, "organizations");
                  const q = query(
                    orgsRef,
                    where("state", "==", value),
                    orderBy("name"),
                  );
                  const snap = await getDocs(q);
                  const list = snap.docs
                    .map((d) => ({ id: d.id, ...d.data() }))
                    .filter((org) => org.active !== false);
                  setOrganizations(list);
                } catch (fetchError) {
                  console.error("Error fetching organizations:", fetchError);
                  setOrganizations([]);
                } finally {
                  setOrgsLoading(false);
                }
              }}
              required
            >
              <option value="">State</option>
              {US_STATES.map((state) => (
                <option key={state.code} value={state.code}>
                  {state.code} - {state.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Location - hidden for targeted sign-ups */}
        {!isTargetedSignup &&
          selectedState &&
          !orgsLoading &&
          organizations.length > 0 && (
            <div>
              <select
                className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white text-base touch-target"
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                required
              >
                <option value="">Location</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
          )}

        {/* No locations message - hidden for targeted sign-ups */}
        {!isTargetedSignup &&
          selectedState &&
          !orgsLoading &&
          organizations.length === 0 && (
            <div className="text-gray-600 text-sm">
              No locations in this state
            </div>
          )}

        {/* Library Card Number - shown after location is chosen and only if org has auth code */}
        {selectedOrgId &&
          organizations
            .find((o) => o.id === selectedOrgId)
            ?.authentication_code?.trim() && (
            <div>
              <input
                type="text"
                value={libraryCard}
                onChange={(e) => setLibraryCard(e.target.value)}
                placeholder={
                  (organizations.find((o) => o.id === selectedOrgId)
                    ?.authentication_label || "Library Card") + " Number"
                }
                className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base touch-target"
                required
              />
            </div>
          )}

        {/* Passwords - shown after location is chosen */}
        {selectedOrgId && (
          <>
            {/* Password */}
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="New Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent pr-12 text-base touch-target"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 touch-target"
              >
                {showPassword ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            </div>

            {/* Confirm Password */}
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent pr-12 text-base touch-target"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 touch-target"
              >
                {showConfirmPassword ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </>
        )}

        {/* Create Account with Google - uses Google for auth, form data for org/location */}
        <button
          type="button"
          onClick={handleGoogleSignUp}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-3 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed touch-target text-base"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {loading ? "Creating account..." : "Create Account with Google"}
        </button>

        {/* Create Account with Email - uses email/password, no Google */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-primary-500 text-white py-3 px-4 sm:px-6 rounded-lg hover:bg-primary-400 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed touch-target text-base"
        >
          {loading
            ? isAdminSignup
              ? "Creating Admin Account..."
              : "Creating account..."
            : isAdminSignup
              ? "Create Admin Account"
              : "Create Account with Email"}
        </button>
      </form>

      {/* Back to Login Button */}
      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={() => handleFormSwitch("login")}
          className="text-primary-600 hover:text-primary-800 transition-colors text-sm sm:text-base touch-target"
        >
          ← Back to Login
        </button>
      </div>
    </div>
  );

  const renderLoginForm = () => (
    <div className="text-left">
      <h3 className="text-xl sm:text-2xl font-bold text-primary-500 mb-2">
        Welcome to Kuzko History!
      </h3>
      <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
        Fill out the information below to access your account.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
          {error}
        </div>
      )}

      <form className="space-y-3 sm:space-y-4" onSubmit={handleLogin}>
        {/* Email */}
        <div>
          <input
            type="email"
            placeholder="Email"
            value={loginEmail}
            onChange={(e) => setLoginEmail(e.target.value)}
            className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base touch-target"
            required
          />
        </div>

        {/* Password */}
        <div className="relative mb-1">
          <input
            type={showLoginPassword ? "text" : "password"}
            placeholder="Password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent pr-12 text-base touch-target"
            required
          />
          <button
            type="button"
            onClick={() => setShowLoginPassword(!showLoginPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 touch-target"
          >
            {showLoginPassword ? (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            )}
          </button>
        </div>

        {/* Forgot Password Link */}
        <div className="text-left" style={{ marginTop: 0 }}>
          <button
            type="button"
            onClick={() => setShowForgotPassword(true)}
            className="text-gray-700 hover:text-primary-700 transition-colors text-sm sm:text-base touch-target"
          >
            Forgot Password?
          </button>
        </div>

        {/* Sign In with Email Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-primary-500 text-white py-3 px-4 sm:px-6 rounded-lg hover:bg-primary-400 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed touch-target text-base"
        >
          {loading ? "Signing in..." : "Sign In with Email"}
        </button>

        {/* Continue with Google - below Sign In */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-3 px-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed touch-target text-base"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {loading ? "Signing in..." : "Sign in with Google"}
        </button>

        {/* Sign Up Link */}
        <div className="text-center">
          <button
            type="button"
            onClick={() => handleFormSwitch("create")}
            className="text-gray-700 text-xl sm:text-lg hover:text-primary-700 transition-colors touch-target"
          >
            Don't have an account?{" "}
            <span className="text-primary-700 font-bold">Sign Up</span>
          </button>
        </div>

        <p className="mt-4 text-center text-xs leading-relaxed text-gray-500 sm:text-sm">
          Kuzko History uses Google Sign-In to securely sign users in. We only
          use basic Google account information to verify identity and provide
          access to the app.
        </p>
      </form>

      <nav
        className="mt-8 pt-6 border-t border-gray-200 text-center text-xs sm:text-sm text-gray-600"
        aria-label="Legal"
      >
        <a
          href="https://fierocode.com/terms-and-conditions"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:text-primary-800 underline"
        >
          Terms and Conditions
        </a>
        <span className="mx-2 text-gray-400" aria-hidden="true">
          ·
        </span>
        <a
          href="https://fierocode.com/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:text-primary-800 underline"
        >
          Privacy Policy
        </a>
      </nav>
    </div>
  );

  const renderForgotPasswordForm = () => (
    <div className="text-left">
      <h3 className="text-xl sm:text-2xl font-bold text-primary-500 mb-2">
        Reset Password
      </h3>
      <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
        Enter your email address and we'll send you a link to reset your
        password.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
          {error}
        </div>
      )}

      {forgotPasswordSuccess ? (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded text-sm">
          <p className="mb-2">Password reset email sent!</p>
          <p className="text-sm">
            Check your email for a link to reset your password.
          </p>
        </div>
      ) : (
        <form
          className="space-y-3 sm:space-y-4"
          onSubmit={handleForgotPassword}
        >
          {/* Email */}
          <div>
            <input
              type="email"
              placeholder="Email"
              value={forgotPasswordEmail}
              onChange={(e) => setForgotPasswordEmail(e.target.value)}
              className="w-full px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-base touch-target"
              required
            />
          </div>

          {/* Send Reset Button */}
          <button
            type="submit"
            disabled={forgotPasswordLoading}
            className="w-full bg-primary-500 text-white py-3 px-4 sm:px-6 rounded-lg hover:bg-primary-400 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed touch-target text-base"
          >
            {forgotPasswordLoading ? "Sending..." : "Send Reset Link"}
          </button>
        </form>
      )}

      {/* Back to Login Button */}
      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={handleBackToLogin}
          className="text-primary-600 hover:text-primary-800 transition-colors text-sm sm:text-base touch-target"
        >
          ← Back to Login
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Toaster
        position="bottom-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#363636",
            color: "#fff",
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: "#10B981",
              secondary: "#fff",
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: "#EF4444",
              secondary: "#fff",
            },
          },
        }}
      />
      <Router>
        <ScrollToTop />
        <Routes>
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/zoho-oauthgrant" element={<ZohoOAuthGrant />} />
          {user ? (
            <Route
              path="*"
              element={
                <AuthenticatedLayout
                  onLogout={handleLogout}
                  userType={userType}
                  userEmail={user.email}
                >
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/topics" element={<Topics />} />
                    <Route
                      path="/topics/:topic"
                      element={<TopicLessons userType={userType} />}
                    />
                    <Route
                      path="/lessons"
                      element={<Lessons userType={userType} />}
                    />
                    <Route path="/videos" element={<Videos />} />
                    <Route path="/gallery" element={<Artifacts />} />
                    <Route
                      path="/admin/lessons"
                      element={
                        <ProtectedSuperAdminRoute userType={userType}>
                          <AdminLessons />
                        </ProtectedSuperAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/lesson/:lessonId"
                      element={
                        <ProtectedSuperAdminRoute userType={userType}>
                          <LessonEntry />
                        </ProtectedSuperAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/videos"
                      element={
                        <ProtectedAdminRoute userType={userType}>
                          <AdminVideos />
                        </ProtectedAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/video/:videoId"
                      element={
                        <ProtectedAdminRoute userType={userType}>
                          <VideoEntry />
                        </ProtectedAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/organizations"
                      element={
                        <ProtectedAdminRoute userType={userType}>
                          <Organizations />
                        </ProtectedAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/organizations/new"
                      element={
                        <ProtectedAdminRoute userType={userType}>
                          <OrganizationEntry />
                        </ProtectedAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/organizations/:orgId"
                      element={
                        <ProtectedAdminRoute userType={userType}>
                          <OrganizationEntry />
                        </ProtectedAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/users"
                      element={
                        <ProtectedAdminRoute userType={userType}>
                          <Users />
                        </ProtectedAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/settings"
                      element={
                        <ProtectedAdminRoute userType={userType}>
                          <Settings />
                        </ProtectedAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/stats"
                      element={
                        <ProtectedAdminRoute userType={userType}>
                          <Stats />
                        </ProtectedAdminRoute>
                      }
                    />
                    <Route
                      path="/admin-dashboard"
                      element={
                        <ProtectedAdminRoute userType={userType}>
                          <AdminDashboard user={user} userType={userType} />
                        </ProtectedAdminRoute>
                      }
                    />
                    <Route
                      path="/lesson/:lessonId"
                      element={<LessonOverview userType={userType} />}
                    />
                    <Route
                      path="/lesson-steps/:lessonId"
                      element={<LessonSteps userType={userType} />}
                    />
                    <Route path="/video/:videoId" element={<VideoOverview />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </AuthenticatedLayout>
              }
            />
          ) : (
            <>
              <Route
                path="/"
                element={
                  <div className="flex min-h-screen flex-col lg:flex-row">
                    {/* Marketing — left on large screens, top on small */}
                    <aside className="flex w-full flex-col justify-center bg-primary-500 px-6 py-10 text-white sm:px-10 lg:w-1/2 lg:min-h-screen lg:px-12 xl:px-16">
                      <img
                        src={logoWhite}
                        alt="Kuzko History"
                        className="mb-[3.5rem] max-h-[3.5rem] w-auto max-w-full object-contain sm:max-h-[6.75rem]"
                      />
                      <h1 className="mb-4 text-2xl font-bold leading-tight sm:text-3xl lg:text-4xl">
                        The easiest way to bring history education to your
                        library.
                      </h1>
                      <p className="mb-8 max-w-xl text-base leading-relaxed text-white/95 sm:text-lg">
                        Find lessons in Kuzko, push play, and watch how fun and
                        easy teaching history can be.
                      </p>
                      <ul className="max-w-xl space-y-4 text-sm leading-relaxed text-white/95 sm:text-base">
                        <li className="flex gap-3">
                          <span
                            className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-white"
                            aria-hidden
                          />
                          <span>
                            Huge library of history lessons on a variety of
                            topics
                          </span>
                        </li>
                        <li className="flex gap-3">
                          <span
                            className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-white"
                            aria-hidden
                          />
                          <span>
                            Video content and discussion questions means little
                            to no lesson prep.
                          </span>
                        </li>
                        <li className="flex gap-3">
                          <span
                            className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-white"
                            aria-hidden
                          />
                          <span>
                            Activity plans for every lesson allow students to
                            interact with the concepts.
                          </span>
                        </li>
                      </ul>
                    </aside>

                    {/* Sign-in / sign-up */}
                    <div className="flex w-full flex-1 flex-col justify-center bg-white px-4 py-10 sm:px-8 lg:w-1/2 lg:py-12">
                      <div className="mx-auto w-full max-w-md">
                        {currentForm === "create"
                          ? renderCreateAccountForm()
                          : showForgotPassword
                            ? renderForgotPasswordForm()
                            : renderLoginForm()}
                      </div>
                    </div>
                  </div>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </Router>
    </div>
  );
}

export default App;
